import { useState, type CSSProperties } from "react";
import { Private, usePrivacy } from "../../components/Privacy";
import { palette } from "../../lib/finance";
import type { InsightCategory } from "./insight-types";
import { figure, signedFigure, tickerClass } from "./insight-format";

/** Order-book style table: one row per category, with a depth bar showing its size. */
export default function CategoryInsights({ rows, currency, onOpenCategory, baseline }: {
  baseline: string; rows: InsightCategory[]; currency: string; onOpenCategory: (category: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const { hidden } = usePrivacy();
  const visible = expanded ? rows : rows.slice(0, 8);
  const max = Math.max(...rows.map((r) => r.amount ?? 0), 1);
  return (
    <section className="panel insight-panel">
      <div className="section-heading"><div><h2>Where spending changed</h2><p>Against {baseline} · {currency}</p></div></div>
      {!rows.length ? <p className="insight-empty">No household spending recorded for either month.</p> : (
        <div className="insight-book insight-category-list">
          <div className="insight-book-head" aria-hidden="true"><span>Category</span><span>Share</span><span>Spent</span><span>Change</span></div>
          {visible.map((row, i) => {
            const style = {
              "--depth": hidden ? "0%" : `${Math.max(0, ((row.amount ?? 0) / max) * 100)}%`,
              "--tone": palette[i % palette.length],
            } as CSSProperties;
            return (
              <button type="button" className="insight-book-row" key={row.name} style={style} onClick={() => onOpenCategory(row.name)} aria-label={`View ${row.name} transactions`}>
                <span className="insight-book-name"><i aria-hidden="true" />{row.name}</span>
                <span className="insight-book-muted"><Private>{row.share === null ? "—" : `${row.share.toFixed(0)}%`}</Private></span>
                <span><Private>{figure(row.amount, currency)}</Private></span>
                <span className={`insight-ticker ${tickerClass(row.change)}`}><Private>{row.change === 0 ? "0.00" : signedFigure(row.change, currency)}</Private></span>
              </button>
            );
          })}
        </div>
      )}
      {rows.length > 8 && <button type="button" className="insight-text-button" aria-expanded={expanded} onClick={() => setExpanded(!expanded)}>{expanded ? "Show fewer" : `Show all ${rows.length} categories`}</button>}
      <p className="insight-note">Split purchases contribute to each category’s household share.</p>
    </section>
  );
}
